import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styles from '../../styles/SubscriptionContainer.module.scss';
import Button from '../../../../components/Button';
import { usePurchaseSubscriptionContext } from '../../../PurchaseSubscription/context/PurchaseSubscriptionContext';
import { zendeskCancellationReason } from '../../../../types/gql-op-types';
import { inAppCancellation as events, troubleshoot as troubleshoot_events } from '../../../../lib/analytics/events';
import { SubscriptionAppBarSpacer } from '../../components/SubscriptionAppBar';
import { BaseInAppCancellationState } from '../util/types';

// Can't pass functions as state, so built a map that can be extended.
// Will need to revisit this if events need to be more custom.
const RequestReasonToEvent = {
  cancellation: events.cancellationTicketConfirmationDoneClicked,
  replacement: events.replacementTicketConfirmationDoneClicked,
  troubleshooting: troubleshoot_events.troubleshootingTicketConfirmationDoneClicked,
};

export enum RequestReason {
  Cancellation = 'cancellation',
  Replacement = 'replacement',
  Troubleshooting = 'troubleshooting',
}

interface RequestSentState extends BaseInAppCancellationState {
  requestType: RequestReason;
  cancellationReason?: zendeskCancellationReason;
  replacementReason?: string;
  troubleshootingReason?: zendeskCancellationReason;
}

export default function RequestSent() {
  const { t } = useTranslation();
  const { device } = usePurchaseSubscriptionContext();
  const location = useLocation<RequestSentState>();
  const { requestType, cancellationReason, replacementReason, troubleshootingReason, subscriptionCategory } =
    location.state;
  events.pageLoad(
    { petId: device.pet!.id, cancellationReason, replacementReason, subscriptionCategory },
    'Request Received',
  );

  const petName = device.pet?.name ? device.pet.name : t('your_dog');

  if (
    (requestType === RequestReason.Cancellation && !cancellationReason) ||
    (requestType === RequestReason.Replacement && !replacementReason) ||
    (requestType === RequestReason.Troubleshooting && !troubleshootingReason)
  ) {
    throw new Error(t('must_provide_reason_for_request'));
  }

  return (
    <>
      <div className={styles.fullWidthWrapper}>
        <SubscriptionAppBarSpacer />
        <div className={styles.subscriptionContainer}>
          <h1>{t('well_be_in_touch_soon')}</h1>

          {requestType === RequestReason.Cancellation ? (
            <>
              <p>
                {t(
                  'your_request_has_been_logged_well_reach_out_via_email_within_a_few_business_hours_to_complete_pet_name_membership_cancellation',
                  {
                    petName,
                  },
                )}{' '}
                <strong>
                  {t('you_will_need_to_confirm_your_cancellation_with_a_fi_customer_support_representative')}
                </strong>
              </p>

              <p>
                {t('in_the_meantime_your_membership_will_stay_active_and_youll_continue_to_have_access_to_the_fi_app')}
              </p>
            </>
          ) : (
            <p>
              {t(
                'we_have_received_your_request_for_request_type_and_our_support_team_will_be_reaching_out_with_next_steps',
                {
                  requestType: requestType === RequestReason.Replacement ? t('replacement') : t('troubleshooting'),
                },
              )}
            </p>
          )}
        </div>
        <div className={styles.buttonContainer}>
          <Button
            className={styles.button}
            onClick={() => {
              RequestReasonToEvent[requestType]({
                petId: device.pet?.id,
                replacementReason: replacementReason,
                cancellationReason: cancellationReason,
                troubleshootingReason: troubleshootingReason,
              });
              window.open('fi://closeappview');
            }}
          >
            {t('got_it')}
          </Button>
          {/* Spacer ensures primary button is aligned with buttons on neighboring pages in in-app cancel flow */}
          <div className={styles.buttonSpacer}></div>
        </div>
      </div>
    </>
  );
}
